import { createRef, useEffect, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import type { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import type { ILine } from '../../../../../models/line.model';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import { TranslationService } from '../../../../../services/translation.service';
import { AnalyticsService } from '../../../../../services/analytics-service';
import LineListShowcase from '../../../../Shared/LineListShowcase';
import EpiserverHelper from '../../../../../helpers/episerver.helper';
import { TrafficInformationFeedbackService } from '../../../../../services/traffic-information-feedback.service';
import TrafficSituationSeverityIcon from '../../../components/TrafficSituationSeverityIcon';
import thumbUpIcon from '../../../../../../assets/icons/thumb-up-blue-1000.svg';
import thumbDownIcon from '../../../../../../assets/icons/thumb-down-blue-1000.svg';
import DataUrlHelper from '../../../../../helpers/data-url.helper';

function createLastUpdatedTimeText(trafficSituation: ITrafficSituation) {
  const today = DateTime.now().startOf('day');
  const yesterday = DateTime.now().startOf('day').minus({ days: 1 });
  const creationDate = trafficSituation.creationTime.startOf('day');
  if (creationDate.equals(today)) {
    return `${TranslationService.get('trafficsituationspage.lastupdatedtimeprefix')} ${TranslationService.get('common.today').toLocaleLowerCase()} ${trafficSituation.creationTime.toFormat('HH:mm')}`;
  }
  if (creationDate.equals(yesterday)) {
    return `${TranslationService.get('trafficsituationspage.lastupdatedtimeprefix')} ${TranslationService.get('common.yesterday').toLocaleLowerCase()} ${trafficSituation.creationTime.toFormat(
      'HH:mm'
    )}`;
  }
  return `${TranslationService.get('trafficsituationspage.lastupdatedtimeprefix')} ${trafficSituation.creationTime.toFormat('yyyy-MM-dd HH:mm')}`;
}

function createValidityTimeText(trafficSituation: ITrafficSituation) {
  if (!trafficSituation.startTime && !trafficSituation.endTime) return undefined;
  let formatString = 'EEEE d MMMM';

  if (trafficSituation.startTime?.year !== trafficSituation.endTime?.year) {
    formatString += ' yyyy';
  }

  return `${TranslationService.get('trafficsituationspage.validitytimeprefix')} ${trafficSituation.startTime?.toFormat(formatString)} - ${trafficSituation?.endTime.toFormat(formatString)}`;
}

function TrafficSituation({ trafficSituation }: { trafficSituation: ITrafficSituation }) {
  // STATE
  const [feedbackSent, setFeedbackSent] = useState(false);

  // VARIABLES
  const distinctLines: ILine[] = TrafficSituationHelper.getDistictLines(trafficSituation);
  const lastUpdatedText = createLastUpdatedTimeText(trafficSituation);
  const validityTimeText = createValidityTimeText(trafficSituation);
  const trafficSituationRef = createRef<HTMLHeadingElement>();
  const feedbackCollectionEnabled = TrafficInformationFeedbackService.isEnabled();

  // === EVENT HANDLERS ===
  function handleShowMoreDetailsClick() {
    AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på visa mer information', '');
  }

  // === EFFECTS ===
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (!('IntersectionObserver' in window && feedbackCollectionEnabled && trafficSituationRef.current && window.matchMedia('(max-width: 500px)').matches)) {
      return () => {};
    }

    const observer = new IntersectionObserver(
      (trafficSituationsEntries) => {
        if (!trafficSituationsEntries || trafficSituationsEntries.length === 0) return;
        const entry = trafficSituationsEntries[0];
        if (entry.isIntersecting) {
          timeoutId = setTimeout(() => {
            TrafficInformationFeedbackService.registerImplicitFeedback(trafficSituation);
          }, 5000);
        } else {
          clearTimeout(timeoutId);
        }
      },
      { threshold: 0.9 }
    );

    observer.observe(trafficSituationRef.current);

    const cleanUpReference = trafficSituationRef.current;
    return () => {
      if (cleanUpReference) {
        observer.unobserve(cleanUpReference);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [feedbackCollectionEnabled, trafficSituationRef, trafficSituation]);

  return (
    <div className="traffic-situation-one-dot-one p-3 print-no-break" ref={trafficSituationRef}>
      <div>
        <div>
          <div className="d-flex justify-content-between mb-2">
            <h3 className="h4 d-flex align-items-center text-nowrap mb-0">
              <TrafficSituationSeverityIcon severity={trafficSituation.severity} className="me-1"></TrafficSituationSeverityIcon>
              <span className="text-wrap">{TranslationService.get(`trafficsituationspage.trafficsituation.${trafficSituation.severity}.long`)}</span>
            </h3>
          </div>
          {distinctLines.length > 0 && <LineListShowcase lines={distinctLines} />}
        </div>
        <div className="font-italic mt-2">{lastUpdatedText}</div>
        {!!trafficSituation.title && (
          <div className="mt-2">
            {trafficSituation.title}
            {EpiserverHelper.isInEditMode() && <span className="fw-bold"> ({trafficSituation.situationNumber})</span>}
          </div>
        )}
        {!!trafficSituation.description && <div className="mt-2">{trafficSituation.description}</div>}
        {!!validityTimeText && <div className="mt-2">{validityTimeText}</div>}
        {trafficSituation.relativeDetailsUrl && (
          <div className="mt-2">
            <a href={trafficSituation.relativeDetailsUrl} onClick={handleShowMoreDetailsClick}>
              {TranslationService.get('trafficsituationspage.linktodetailspagetext')}
            </a>
          </div>
        )}
        {feedbackCollectionEnabled && (
          <div>
            <hr />
            {!feedbackSent && (
              <div className="d-flex">
                <span className="mr-2">{TranslationService.get('trafficsituationspage.explicitfeedbackquestiontext')}</span>
                <div className="traffic-situation-one-dot-one__explicit-feedback-buttons-wrapper text-nowrap">
                  <button
                    className="btn btn-link traffic-situation-one-dot-one__explicit-feedback-button p-0"
                    onClick={() => {
                      TrafficInformationFeedbackService.registerExplicitFeedback(trafficSituation, 1);
                      setFeedbackSent(true);
                    }}
                  >
                    <img src={DataUrlHelper.encode(thumbUpIcon)} alt={TranslationService.get('common.yes')} />
                  </button>
                  <button
                    className="btn btn-link traffic-situation-one-dot-one__explicit-feedback-button p-0"
                    onClick={() => {
                      TrafficInformationFeedbackService.registerExplicitFeedback(trafficSituation, -1);
                      setFeedbackSent(true);
                    }}
                  >
                    <img src={DataUrlHelper.encode(thumbDownIcon)} alt={TranslationService.get('common.no')} />
                  </button>
                </div>
              </div>
            )}
            {feedbackSent && <div className="">{TranslationService.get('trafficsituationspage.explicitfeedbackreceivedtext')}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrafficSituation;
